import React, { useState, useEffect } from 'react';
import { losApi, PARTY_ROLE_LABELS } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { errorMessage } from '../../errors';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { Users, Link2, Copy, RefreshCw, Plus, Trash2, Mail, Ban, Pencil, MessageSquare } from 'lucide-react';
import { ModuleHeader, Fld, SelectFld } from '../shared/formFields';
import { copyText } from '../../clipboard';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '../../../components/ui/dialog';
import { formatPhone } from '../../shared/phone';
import { whyNotSent } from '../../shared/whyNotSent';
import { formatDate } from '../../kit/time';

// Roles that can be seated on the deal thread (deal_messages_router.THREAD_PARTY_ROLES).
const THREAD_ROLES = ['title_company', 'escrow'];

export function PartiesTab({ loan, onChange }) {
  const [parties, setParties] = useState(null); // null until the list has loaded
  const [loadError, setLoadError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const load = async () => {
    try { setParties(await losApi.listParties(loan.loan_file_id)); setLoadError(null); }
    catch (e) { setLoadError(errorMessage(e, "Couldn't load the parties")); }
  };
  useEffect(() => { setParties(null); setLoadError(null); load(); }, [loan.loan_file_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (pid) => {
    if (!(await confirmDialog({ title: 'Remove this party?', confirmText: 'Remove', destructive: true }))) return;
    try { await losApi.deleteParty(pid); await load(); }
    catch (e) { toast({ title: 'Remove failed', description: errorMessage(e) }); }
  };
  const [sendingParty, setSendingParty] = useState(null);
  const [threadBusy, setThreadBusy] = useState(null);
  const addToThread = async (p) => {
    // Seating them emails an invite to someone outside the company: confirm first.
    if (!(await confirmDialog({ title: 'Add to the deal thread?', description: `${p.name} will get an email at ${p.email} and can read and post in this deal's message thread. They will not see documents or the rest of the loan.`, confirmText: 'Add and email' }))) return;
    setThreadBusy(p.party_id);
    try {
      const r = await losApi.addPartyToThread(p.party_id);
      if (r.email_sent) toast({ title: 'Added to the thread', description: `Invite sent to ${r.sent_to}` });
      else toast({ title: 'Added, but no invite was sent', description: whyNotSent(r.reason) });
      await load();
    } catch (e) { toast({ title: 'Could not add to the thread', description: errorMessage(e) }); }
    finally { setThreadBusy(null); }
  };
  const removeFromThread = async (p) => {
    if (!(await confirmDialog({ title: 'Remove from the deal thread?', description: `${p.name} loses access to this deal's messages. The party stays on the loan.`, confirmText: 'Remove', destructive: true }))) return;
    setThreadBusy(p.party_id);
    try { await losApi.removePartyFromThread(p.party_id); await load(); }
    catch (e) { toast({ title: 'Could not remove from the thread', description: errorMessage(e) }); }
    finally { setThreadBusy(null); }
  };
  const emailParty = async (p) => {
    // A real email to someone outside the company: one stray click must not send it.
    if (!(await confirmDialog({ title: 'Email the status link?', description: `This sends an email to ${p.email}.`, confirmText: 'Send email' }))) return;
    setSendingParty(p.party_id);
    try {
      const r = await losApi.sendPartyLink(p.party_id);
      if (r.email_sent) { toast({ title: 'Link emailed', description: `Sent to ${r.sent_to}` }); await load(); }
      else toast({ title: 'Link not sent', description: `${whyNotSent(r.reason)} Use Copy on the status link instead.` });
    } catch (e) {
      toast({ title: 'Link not sent', description: errorMessage(e) });
    } finally { setSendingParty(null); }
  };
  const regen = async () => {
    if (!(await confirmDialog({ title: 'Regenerate the share link?', description: 'Any previously distributed link will stop working.', confirmText: 'Regenerate', destructive: true }))) return;
    try {
      await losApi.regenerateShareToken(loan.loan_file_id); onChange();
      toast({ title: 'Share link rotated' });
    } catch (e) { toast({ title: 'Regenerate failed', description: errorMessage(e) }); }
  };
  const revoked = !!loan.share_token_revoked_at;
  const revoke = async () => {
    if (!(await confirmDialog({ title: 'Turn off the share link?', description: 'The link stops working now. The loan is not affected. Rotate it to turn sharing back on.', confirmText: 'Turn off', destructive: true }))) return;
    try {
      await losApi.revokeShareToken(loan.loan_file_id); onChange();
      toast({ title: 'Share link turned off' });
    } catch (e) { toast({ title: 'Turn off failed', description: errorMessage(e) }); }
  };
  const sendEmail = async () => {
    const to = emailTo.trim();
    if (!to) return;
    // A real email to someone outside the company: one stray click must not send it.
    if (!(await confirmDialog({ title: 'Email the status link?', description: `This sends an email to ${to}.`, confirmText: 'Send email' }))) return;
    setSendingEmail(true);
    try {
      const r = await losApi.sendShareLink(loan.loan_file_id, to);
      if (r.email_sent) { toast({ title: 'Link emailed', description: `Sent to ${r.sent_to}` }); setEmailTo(''); }
      else toast({ title: 'Link not sent', description: `${whyNotSent(r.reason)} Use Copy instead.` });
    } catch (e) {
      toast({ title: 'Link not sent', description: errorMessage(e) });
    } finally { setSendingEmail(false); }
  };
  // The borrower's own portal link (deal messaging). Fetched on click, not on open:
  // asking for it creates it the first time.
  const [borrowerBusy, setBorrowerBusy] = useState(false);
  const borrowerUrl = (doc) => `${window.location.origin}${doc.url_path}`;
  const copyBorrowerLink = async () => {
    setBorrowerBusy(true);
    try {
      const url = borrowerUrl(await losApi.getBorrowerLink(loan.loan_file_id));
      if (await copyText(url)) toast({ title: 'Copied' });
      else toast({ title: 'Copy failed', description: 'Open the loan from Deals to copy it by hand' });
    } catch (e) { toast({ title: 'Could not get the borrower link', description: errorMessage(e) }); }
    finally { setBorrowerBusy(false); }
  };
  const regenBorrowerLink = async () => {
    if (!(await confirmDialog({ title: 'Regenerate the borrower portal link?', description: 'The borrower\'s current link stops working at once. Copy the new one and send it to them.', confirmText: 'Regenerate', destructive: true }))) return;
    setBorrowerBusy(true);
    try {
      const url = borrowerUrl(await losApi.regenerateBorrowerLink(loan.loan_file_id));
      const copied = await copyText(url);
      toast({ title: 'Borrower portal link replaced', description: copied ? 'The new link is copied.' : 'Use Copy link to get the new one.' });
    } catch (e) { toast({ title: 'Regenerate failed', description: errorMessage(e) }); }
    finally { setBorrowerBusy(false); }
  };
  const shareUrl = `${window.location.origin}/loan/${loan.share_token}`;
  const copy = async () => {
    if (await copyText(shareUrl)) toast({ title: 'Copied' });
    else toast({ title: 'Copy failed', description: 'Select the text and copy it manually' });
  };

  return (
    <div className="space-y-4">
      <ModuleHeader title="Parties & Sharing" />
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <Link2 size={14} style={{ color: '#6BA82E' }} />
          <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>Public Loan Status Link</div>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">Share with borrower / realtor / title / insurance — live loan status, no login. PII redacted.</p>
        {revoked && (
          <p className="text-[11px] text-red-600 mb-3" data-testid="share-revoked-note">This link is turned off. Rotate it to make a new link and turn sharing back on.</p>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`flex-1 min-w-0 font-mono text-[11px] bg-slate-50 rounded-lg px-3 py-2 border border-slate-100 truncate ${revoked ? 'line-through text-slate-400' : ''}`} data-testid="share-url">{shareUrl}</div>
          <button onClick={copy} disabled={revoked} data-testid="copy-share-btn" className="text-[12px] font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: '#030524' }}><Copy size={12} /> Copy</button>
          <button onClick={regen} className="text-[12px] font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5"><RefreshCw size={12} /> Rotate</button>
          {!revoked && (
            <button onClick={revoke} data-testid="revoke-share-btn" className="text-[12px] font-semibold px-3 py-2 rounded-lg border border-slate-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5"><Ban size={12} /> Turn off</button>
          )}
        </div>
        {!revoked && (
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="Email the link to…" data-testid="share-email-input"
              className="flex-1 min-w-0 w-auto text-[12px] md:text-[12px]" />
            <button onClick={sendEmail} disabled={sendingEmail || !emailTo.trim()} data-testid="share-email-btn" className="text-[12px] font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50"><Mail size={12} /> Email</button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <Link2 size={14} style={{ color: '#6BA82E' }} />
          <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>Borrower Portal Link</div>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">The borrower's own link to this loan and the message thread with your team. Regenerate it if it was sent to the wrong place.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={copyBorrowerLink} disabled={borrowerBusy} data-testid="copy-borrower-link-btn" className="text-[12px] font-semibold px-3 py-2 rounded-lg text-white inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: '#030524' }}><Copy size={12} /> Copy link</button>
          <button onClick={regenBorrowerLink} disabled={borrowerBusy} data-testid="regen-borrower-link-btn" className="text-[12px] font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5 disabled:opacity-50"><RefreshCw size={12} /> Regenerate</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex justify-between items-center mb-3">
          <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>Transaction Parties{parties ? ` (${parties.length})` : ''}</div>
          <button onClick={() => setShowAdd(true)} data-testid="add-party-btn" className="text-[12px] font-semibold px-3 py-1.5 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: '#070B5F' }}><Plus size={12} /> Add Party</button>
        </div>
        {loadError && (
          <div className="flex items-center gap-3 text-[12px] text-red-600" data-testid="parties-load-error">
            <span>{loadError}</span>
            <button onClick={load} data-testid="parties-retry" className="font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50">Retry</button>
          </div>
        )}
        {!parties && !loadError && <div className="text-[12px] text-slate-400" data-testid="parties-loading">Loading…</div>}
        {parties && (
          <div className="space-y-2">
            {parties.map((p) => (
              <div key={p.party_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100" data-testid={`party-${p.party_id}`}>
                <Users size={14} style={{ color: '#6BA82E' }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold" style={{ color: '#030524' }}>{p.name} <span className="text-[11px] font-normal text-slate-400 ml-1">({PARTY_ROLE_LABELS[p.role]})</span></div>
                  <div className="text-[11px] text-slate-500">{p.company && `${p.company} · `}{p.email}{p.phone && ` · ${formatPhone(p.phone)}`}</div>
                  <PartyFlags party={p} />
                </div>
                {THREAD_ROLES.includes(p.role) && (p.thread_user_id ? (
                  <span className="inline-flex items-center gap-1.5">
                    <span data-testid={`on-thread-${p.party_id}`} className="text-[10px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">On deal thread</span>
                    <button
                      onClick={() => removeFromThread(p)}
                      disabled={threadBusy === p.party_id}
                      aria-label={`Remove ${p.name} from the deal thread`}
                      data-testid={`remove-thread-${p.party_id}`}
                      className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    >Remove from thread</button>
                  </span>
                ) : (
                  <button
                    onClick={() => addToThread(p)}
                    disabled={!p.email || threadBusy === p.party_id}
                    title={p.email ? 'Add to the deal message thread' : 'Add an email first'}
                    aria-label={`Add ${p.name} to the deal thread`}
                    data-testid={`add-thread-${p.party_id}`}
                    className="text-[11px] font-semibold px-2 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 inline-flex items-center gap-1"
                  ><MessageSquare size={11} /> {p.email ? 'Add to deal thread' : 'Add an email first'}</button>
                ))}
                <button
                  onClick={() => emailParty(p)}
                  disabled={!p.email || !p.can_view_status || revoked || sendingParty === p.party_id}
                  title={!p.email ? 'Add an email to send the link' : !p.can_view_status ? 'This party is not set to see the status link' : revoked ? 'The status link is turned off' : 'Email the status link'}
                  aria-label={`Email the status link to ${p.name}`}
                  data-testid={`email-party-${p.party_id}`}
                  className="text-slate-400 hover:text-slate-700 disabled:opacity-40 disabled:hover:text-slate-400"
                ><Mail size={13} /></button>
                <button onClick={() => setEditing(p)} aria-label={`Edit ${p.name}`} data-testid={`edit-party-${p.party_id}`} className="text-slate-400 hover:text-slate-700"><Pencil size={13} /></button>
                <button onClick={() => del(p.party_id)} aria-label={`Remove ${p.name}`} className="text-slate-400 hover:text-red-500"><Trash2 size={13} /></button>
              </div>
            ))}
            {parties.length === 0 && <div className="text-[12px] text-slate-400 italic">No parties added yet.</div>}
          </div>
        )}
      </div>
      {showAdd && <PartyModal loanId={loan.loan_file_id} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <PartyModal loanId={loan.loan_file_id} party={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// What the loan officer set on a party, shown under its name.
function PartyFlags({ party }) {
  const flags = [];
  if (!party.can_view_status) flags.push('Status link off');
  if (party.send_updates) flags.push('Gets updates');
  if (party.link_sent_at) flags.push(`Link emailed ${formatDate(party.link_sent_at)}`);
  if (!flags.length) return null;
  return <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`party-flags-${party.party_id}`}>{flags.join(' · ')}</div>;
}

function Check({ checked, onChange, children, testId }) {
  return (
    <label className="flex items-start gap-2 text-[12px] text-slate-700">
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
      <span>{children}</span>
    </label>
  );
}

// Add a party, or edit one (`party` set). The flags are the loan officer's choices.
function PartyModal({ loanId, party, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    role: party?.role || 'buyer_agent',
    name: party?.name || '',
    company: party?.company || '',
    email: party?.email || '',
    phone: party?.phone || '',
    can_view_status: party?.can_view_status ?? true,
    can_receive_conditions: party?.can_receive_conditions ?? true,
    send_updates: party?.send_updates ?? false,
  }));
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF(x => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) return;
    if (f.send_updates && !f.email.trim()) {
      toast({ title: 'Add an email first', description: 'Updates go to the email address on this party.' });
      return;
    }
    setSaving(true);
    try {
      if (party) await losApi.updateParty(party.party_id, f);
      else await losApi.addParty(loanId, f);
      onSaved();
    } catch (e) {
      toast({ title: party ? 'Could not save the party' : 'Could not add the party', description: errorMessage(e) });
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogTitle className="font-serif text-lg font-bold mb-4" style={{ color: '#030524' }}>{party ? 'Edit Transaction Party' : 'Add Transaction Party'}</DialogTitle>
        <div className="space-y-3">
          <SelectFld label="Role" value={f.role} onChange={set('role')} options={Object.entries(PARTY_ROLE_LABELS)} />
          <Fld label="Name" value={f.name} onChange={set('name')} testId="party-name" />
          <Fld label="Company" value={f.company} onChange={set('company')} />
          <Fld label="Email" value={f.email} onChange={set('email')} testId="party-email" />
          <Fld label="Phone" kind="phone" value={f.phone} onChange={set('phone')} />
          <div className="space-y-2 pt-1">
            <Check checked={f.can_view_status} onChange={set('can_view_status')} testId="party-can-view-status">Can see the loan status link</Check>
            <Check checked={f.can_receive_conditions} onChange={set('can_receive_conditions')} testId="party-can-receive-conditions">Can receive conditions</Check>
            <Check checked={f.send_updates} onChange={set('send_updates')} testId="party-send-updates">
              Send updates by email
              <span className="block text-[11px] text-slate-400">Application received, conditionally approved, clear to close and funded. Nothing goes out until milestone emails are switched on for the workspace.</span>
            </Check>
          </div>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving} className="text-sm px-4 py-2 rounded-lg text-white disabled:opacity-50" style={{ background: '#070B5F' }} data-testid="save-party">Save</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
